/**
 * Fetches an image and returns it as a base64 data URL.
 *
 * html2canvas silently drops cross-origin images (e.g. S3 logos) unless the
 * bucket sends CORS headers, so PDF generators inline the logo through this
 * helper before rendering. Returns null when the URL is missing or the fetch
 * fails, so callers can fall back to rendering without a logo.
 *
 * @param {string} url - Image URL (optional)
 * @returns {Promise<string|null>} Data URL or null
 */
const WHITE_THRESHOLD = 245;

/**
 * Crops away the white/transparent margins of an image so the visible content
 * fills its box. Logos are often uploaded on a large white canvas, which makes
 * them render tiny and misaligned in print/PDF templates. Returns the original
 * data URL when trimming is not possible or nothing would be cropped.
 *
 * @param {string} dataUrl - Image as a data URL
 * @returns {Promise<string>} Trimmed image as a data URL
 */
export const trimImageWhitespace = (dataUrl) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      // Flatten transparency onto white so it counts as background.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
      let minX = canvas.width; let minY = canvas.height; let maxX = -1; let maxY = -1;
      for (let y = 0; y < canvas.height; y++) {
        for (let x = 0; x < canvas.width; x++) {
          const i = (y * canvas.width + x) * 4;
          if (data[i] < WHITE_THRESHOLD || data[i + 1] < WHITE_THRESHOLD || data[i + 2] < WHITE_THRESHOLD) {
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      }

      if (maxX < 0 || (minX === 0 && minY === 0 && maxX === canvas.width - 1 && maxY === canvas.height - 1)) {
        resolve(dataUrl);
        return;
      }

      const cropped = document.createElement('canvas');
      cropped.width = maxX - minX + 1;
      cropped.height = maxY - minY + 1;
      cropped.getContext('2d').drawImage(canvas, minX, minY, cropped.width, cropped.height, 0, 0, cropped.width, cropped.height);
      resolve(cropped.toDataURL('image/png'));
    } catch (error) {
      resolve(dataUrl);
    }
  };
  img.onerror = () => resolve(dataUrl);
  img.src = dataUrl;
});

/**
 * Fetches a logo and returns it as a whitespace-trimmed data URL, ready to be
 * embedded in print/PDF templates. Returns null when there is no logo or it
 * cannot be loaded.
 *
 * @param {string} url - Logo URL (optional)
 * @returns {Promise<string|null>} Trimmed data URL or null
 */
export const prepareLogoDataUrl = async (url) => {
  const dataUrl = await fetchImageAsDataUrl(url);
  return dataUrl ? trimImageWhitespace(dataUrl) : null;
};

export const fetchImageAsDataUrl = async (url) => {
  if (!url) return null;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('Could not load image for PDF:', error);
    return null;
  }
};
